import axios from 'axios';
import type {AxiosError, AxiosResponse} from 'axios';
import {setSession} from './jwt';
import env from '@/constants/env';
import {decodeJWT} from '@/components/utils/helper';

const baseURL = env.ApiServerUrl || window.location.origin;

const axiosInstance = axios.create({
  baseURL: baseURL
});

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
const failedRequestsQueue: Array<{
  onSuccess: (token: string) => void;
  onFailure: (error: AxiosError) => void;
}> = [];

export const refreshToken = async () => {
  const refresh = localStorage.getItem('refresh');
  const organizationPayload = localStorage.getItem('organization');

  if (!refresh || !organizationPayload) {
    throw new Error('Missing required tokens');
  }

  let organizationId;
  try {
    organizationId = JSON.parse(organizationPayload).id;
  } catch {
    throw new Error('Invalid organization data');
  }

  const response = await axios.post(`${baseURL}/api/auth/token/refresh/`, {
    organization: organizationId,
    refresh
  });

  const {
    access: newAccess,
    refresh: newRefresh,
    access_expiration: newAccessExpiration,
    refresh_expiration: newRefreshExpiration
  } = response.data;

  const updates = [
    ['accessToken', newAccess],
    ['accessTokenExpiration', newAccessExpiration],
    ['refresh', newRefresh],
    ['refreshExpiration', newRefreshExpiration]
  ];

  updates.forEach(([key, value]) => localStorage.setItem(key, value));
  setSession(newAccess);

  const user = JSON.parse(localStorage.getItem('user'));
  const organization = decodeJWT(newAccess)?.organization;
  localStorage.setItem(
    'user',
    JSON.stringify({...user, organization: organization})
  );
  localStorage.setItem('organization', JSON.stringify(organization));
  return newAccess;
};

const updateRequestAuth = (request: any, token: string) => {
  request.headers.Authorization = `Bearer ${token}`;
  if (request.data && typeof request.data === 'string') {
    request.data = JSON.parse(request.data);
  }
  request._alreadyRefreshed = true;
};

const handleRefreshFailure = (error: any) => {
  const requests = failedRequestsQueue.splice(0);
  requests.forEach(request => request.onFailure(error));
  localStorage.clear();
  window.location.href = '/authentication/login';
};

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: any) => {
    const originalRequest = error.config;
    const isUnauthorized =
      error.response?.status === 401 ||
      error.response?.data?.code === 'token_not_valid';

    if (
      !isUnauthorized ||
      originalRequest._retry ||
      originalRequest._alreadyRefreshed
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedRequestsQueue.push({
          onSuccess: (token: string) => {
            updateRequestAuth(originalRequest, token);
            resolve(axiosInstance(originalRequest));
          },
          onFailure: reject
        });
      });
    }

    isRefreshing = true;

    try {
      const newAccessToken = await refreshToken();
      updateRequestAuth(originalRequest, newAccessToken);

      // Process queued requests with new token
      const requests = failedRequestsQueue.splice(0);
      requests.forEach(request => request.onSuccess(newAccessToken));

      return axiosInstance(originalRequest);
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      handleRefreshFailure(refreshError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  }
);

// Track the requests in flight, so that less important data can wait
// until the page has loaded what it shows first.
const IDLE_QUIET_MS = 300;
let pendingRequests = 0;
let idleTimer: ReturnType<typeof setTimeout> | undefined;
const idleWaiters: Array<() => void> = [];

const checkIdle = () => {
  clearTimeout(idleTimer);
  if (pendingRequests > 0) {
    return;
  }
  idleTimer = setTimeout(() => {
    if (pendingRequests === 0) {
      idleWaiters.splice(0).forEach(resolve => resolve());
    }
  }, IDLE_QUIET_MS);
};

const requestDone = () => {
  pendingRequests = Math.max(pendingRequests - 1, 0);
  checkIdle();
};

axiosInstance.interceptors.request.use(config => {
  pendingRequests += 1;
  clearTimeout(idleTimer);
  return config;
});

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    requestDone();
    return response;
  },
  (error: AxiosError) => {
    requestDone();
    return Promise.reject(error);
  }
);

/**
 * Resolve once no API requests have been in flight for a short moment,
 * or after `maxWaitMs` when the page keeps requesting.
 */
export const whenRequestsIdle = (maxWaitMs = 8000) =>
  new Promise<void>(resolve => {
    const fallback = setTimeout(resolve, maxWaitMs);
    idleWaiters.push(() => {
      clearTimeout(fallback);
      resolve();
    });
    checkIdle();
  });

export default axiosInstance;
